import { addDays, format, parseISO, set } from "date-fns";
import { AvailabilitySlot, Service, TimeRange, WeekDay } from "./types";
import { CLINIC_SCHEDULES, weekDaysTranslation } from "../../../utils/constants";
import { capitalize, uniqBy } from "lodash";

export const createInitialTimeRanges = (
    date: Date,
    dayPeriod: "morning" | "afternoon" | "both",
    clinicSchedules: typeof CLINIC_SCHEDULES
) => {
    const ranges: TimeRange[] = [];
    const { morning, afternoon } = clinicSchedules;

    if (dayPeriod === "morning" || dayPeriod === "both") {
        ranges.push({
            start: set(date, morning.start),
            end: set(date, morning.end),
        });
    }
    if (dayPeriod === "afternoon" || dayPeriod === "both") {
        ranges.push({
            start: set(date, afternoon.start),
            end: set(date, afternoon.end),
        });
    }

    return ranges;
};

export const getDayIndex = (day: WeekDay): number => {
    const dayMap: Record<WeekDay, number> = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
    };
    return dayMap[day];
};

export const formatDayAndDate = (day: WeekDay, weekStartDate: Date) => {
    const capitalizedDay = weekDaysTranslation[capitalize(day)];
    const dateNumber = format(addDays(weekStartDate, getDayIndex(day)), "d");
    return `${capitalizedDay}, ${dateNumber}`;
};

export const getSlotsForDay = (
    weekStartDate: Date,
    day: WeekDay,
    availableSlots: AvailabilitySlot[],
    selectedSlots: AvailabilitySlot[]
) => {
    const dayDate = addDays(weekStartDate, getDayIndex(day));

    const selectedSlotsForDay = selectedSlots.filter(
        (slot) => format(parseISO(slot.start_date), "yyyy-MM-dd") === format(dayDate, "yyyy-MM-dd")
    );

    const allSlotsForDay = uniqBy(
        [
            ...availableSlots.filter((slot) => format(parseISO(slot.start_date), "yyyy-MM-dd") === format(dayDate, "yyyy-MM-dd")),
            ...selectedSlotsForDay,
        ],
        "slotId"
    );

    const sortedSlots = allSlotsForDay.sort((a, b) => parseISO(a.start_date).getTime() - parseISO(b.start_date).getTime());

    return sortedSlots;
};

// Helper to map slots to calendar events and resources
export const getCalendarDataForDay = (
    slotsOfTheDay: Array<AvailabilitySlot>,
    selectedSlots: Array<AvailabilitySlot>,
    firstService: Service,
    secondService?: Service
) => {
    const services = [];
    if (firstService) {
        services.push(firstService);
    }
    if (secondService) {
        services.push(secondService);
    }

    const resources = services.map((service) => ({
        resourceId: service.name,
        resourceTitle: service.name,
    }));

    const events = slotsOfTheDay.map((slot) => {
        const startDate = typeof slot.start_date === "string" ? parseISO(slot.start_date) : slot.start_date;
        const endDate = typeof slot.end_date === "string" ? parseISO(slot.end_date) : slot.end_date;

        return {
            id: slot.slotId,
            title: `${slot.serviceName} ${slot.isOnline ? `- Online` : ""}`,
            start: startDate,
            end: endDate,
            resourceId: slot.serviceName,
            slotData: slot,
            isSelected: selectedSlots.some((s) => s.slotId === slot.slotId),
            color: slot.color,
        };
    });

    return { resources, events };
};

export const getFirstSlotDate = (slots: Array<AvailabilitySlot>, weekStartDate: Date, dayIndex: number) => {
    let firstSlotDate;
    if (slots[0]?.start_date) {
        if (typeof slots[0].start_date === "string") {
            firstSlotDate = parseISO(slots[0].start_date);
        } else {
            firstSlotDate = slots[0].start_date;
        }
    } else {
        firstSlotDate = addDays(weekStartDate, dayIndex);
    }
    return firstSlotDate;
};

export const WEEK_DAYS: { value: WeekDay; label: string }[] = [
    { value: "monday", label: "Segunda-feira" },
    { value: "tuesday", label: "Terça-feira" },
    { value: "wednesday", label: "Quarta-feira" },
    { value: "thursday", label: "Quinta-feira" },
    { value: "friday", label: "Sexta-feira" },
    { value: "saturday", label: "Sábado" },
    { value: "sunday", label: "Domingo" },
];

export const isTechnicianFieldDisabled = (selectedService: any, psychologistTreatment: any, clientOwnerTreatment: any) => {
    if (!selectedService) {
        return false;
    }
    // Psy service
    const psychologistCondition = psychologistTreatment && selectedService.id === 34;
    // Reports service
    const clientOwnerCondition = clientOwnerTreatment && (selectedService.id === 37 || selectedService.id === 40);

    const isDisabled = psychologistCondition || clientOwnerCondition;

    return isDisabled;
};
